import axios from 'axios';
import EventBus from '../../layouts/slots/event-bus';
import config from '../../config';
import { rg4js } from '../../lib/raygun';
import { getUserStore, getAppStore } from '@/store';
import router from '../../router';
import { waitForAuthReady } from '../auth/authReady';

// One axios instance per API version, sharing the interceptor set below.
// v2 is the core2/fleet surface; v3 is fleet's client-facing surface
// (clusters today — hidden behind the clusters-fleet flag).
const createApi = version => axios.create({
  baseURL: `${config.VUE_APP_NUCLEUS_API}/api/${version}/`,
  // Axios's default query encoder form-encodes spaces as `+`, but the
  // gateway's grpc_json_transcoder follows RFC 3986 (`+` is a literal plus),
  // so search queries with spaces matched nothing — dev/nucleus#4555.
  // encodeURIComponent sends spaces as %20, which both v1 and v2 decode.
  paramsSerializer: { encode: encodeURIComponent },
});

const clients = {};

// Fallback for bodies with no errorCode (grpc-transcoded fleet errors are
// {code, message}): derive it from the HTTP status so fleet-served responses
// reach the same error page as core2's. Bodies that carry errorCode (core2
// DTOs, the gateway's authz denials) always win.
const httpStatusToErrorCode = {
  401: 'ERR_UNAUTHORIZED',
  403: 'ERR_FORBIDDEN',
  404: 'ERR_NOT_FOUND',
};

const getClientFor = async version => {
  const appStore = getAppStore();

  if (appStore.onlineStatus === false) {
    throw new Error("Currently Offline");
  }

  if (!clients[version]) {
    const client = createApi(version);

    // RESPONSE INTERCEPTOR
    client.interceptors.response.use(
      response => response,
      error => {
        rg4js('send', {
          error,
          message: error.message,
        });
        const errorCodesToEmit = ['ERR_FORBIDDEN', 'ERR_PROVIDER_MISMATCH', 'ERR_NOT_FOUND', 'ERR_UNAUTHORIZED']
        const status = error?.response?.status;
        const code = error?.response?.data?.errorCode
          || httpStatusToErrorCode[status]
          || '';
        const message = error?.response?.data?.message || error.message;
        const errorEvent = {
          statusCode: status,
          code : code,
          message : message
        }
        if(errorCodesToEmit.includes(code)) {
          EventBus.emit('error', errorEvent);
        }
        else {
          return Promise.reject({
                message: message,
          });
        }
      }
    );

    // REQUEST INTERCEPTOR
    client.interceptors.request.use(async config => {
      await waitForAuthReady;

      const userStore = getUserStore();
      const currentUser = userStore.currentUser;

      // Envoy ext_authz (in front of fleet) requires the "Bearer " prefix;
      // other v2 backends accept either, but Bearer is the standard.
      if (currentUser?.getIdToken) {
        const token = await currentUser.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      }

      const org = router.currentRoute.value.query.org;
      if (org) {
        // Org is scoped via the OrganizationId header (matching the v1 API).
        // Fleet reads it from the forwarded gRPC metadata — no query param.
        config.headers.OrganizationId = org;
      }

      // `query` is the free-text search param on every v2 list endpoint;
      // stray leading/trailing whitespace from the search box is never
      // intentional, so trim it once here instead of in each caller.
      if (typeof config.params?.query === 'string') {
        config.params.query = config.params.query.trim();
      }

      return config;
    });

    clients[version] = client;
  }

  return clients[version];
};

const getClient = () => getClientFor('v2');
const getClientV3 = () => getClientFor('v3');

const handleSuccess = async response => ({
  data: response?.data || null,
});

export default {
  getClient,
  getClientV3,
  handleSuccess,
};
